import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, X, Loader2, Image as ImageIcon, AlertCircle } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

/**
 * ImageUploader - Componente centralizzato per upload immagini
 * 
 * Props:
 * - value: string | null - URL dell'immagine corrente
 * - onChange: (imageData) => void - Callback quando l'immagine cambia
 * - onRemove: () => void - Callback quando l'immagine viene rimossa
 * - label: string - Etichetta del campo
 * - aspectRatio: string - Aspect ratio suggerito (es. "16/9", "1/1", "4/5")
 * - showUrlFallback: boolean - Mostra campo URL come fallback
 * - maxSize: number - Dimensione massima in MB (default: 20)
 * - className: string - Classi CSS aggiuntive
 */
export default function ImageUploader({
  value,
  onChange,
  onRemove,
  label = "Immagine",
  aspectRatio = "16/9",
  showUrlFallback = true,
  maxSize = 20,
  className = ""
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUrlInput, setShowUrlInput] = useState(false);
  const [urlInput, setUrlInput] = useState('');

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const rejection = rejectedFiles[0];
      if (rejection.errors[0]?.code === 'file-too-large') {
        toast.error(`File troppo grande. Massimo ${maxSize}MB`);
      } else if (rejection.errors[0]?.code === 'file-invalid-type') {
        toast.error('Formato non supportato. Usa JPEG, PNG o WebP');
      } else {
        toast.error('File non valido');
      }
      return;
    }

    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/admin/upload/image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      const imageData = response.data;
      onChange({
        url: imageData.url,
        thumbnail_url: imageData.thumbnail_url,
        large_url: imageData.large_url,
        original_url: imageData.original_url,
        id: imageData.id,
        width: imageData.width,
        height: imageData.height
      });
      
      toast.success('Immagine caricata');
    } catch (error) {
      console.error('Upload error:', error);
      const message = error.response?.data?.detail || 'Errore nel caricamento';
      toast.error(message);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [onChange, maxSize]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    maxSize: maxSize * 1024 * 1024,
    multiple: false
  });

  const handleUrlSubmit = () => {
    if (urlInput.trim()) {
      onChange({
        url: urlInput.trim(),
        thumbnail_url: urlInput.trim(),
        large_url: urlInput.trim(),
        original_url: urlInput.trim()
      });
      setUrlInput('');
      setShowUrlInput(false);
      toast.success('URL immagine impostato');
    }
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove();
    } else {
      onChange(null);
    }
  };

  // Calculate aspect ratio for preview
  const getAspectClass = () => {
    switch (aspectRatio) {
      case '1/1': return 'aspect-square';
      case '4/5': return 'aspect-[4/5]';
      case '4/3': return 'aspect-[4/3]';
      case '3/2': return 'aspect-[3/2]';
      case '16/9': return 'aspect-video';
      case '21/9': return 'aspect-[21/9]';
      default: return 'aspect-video';
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium mb-2">{label}</label>
      )}

      {/* Current Image Preview */}
      {value && (
        <div className={`relative ${getAspectClass()} bg-muted rounded-sm overflow-hidden mb-3 group`}>
          <img
            src={value}
            alt={label}
            className="w-full h-full object-cover"
          />
          {/* Overlay with actions */}
          <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => document.getElementById(`upload-input-${label}`)?.click()}
            >
              <Upload size={14} className="mr-1" />
              Sostituisci
            </Button>
            <Button
              type="button"
              size="sm"
              variant="destructive"
              onClick={handleRemove}
            >
              <X size={14} className="mr-1" />
              Rimuovi
            </Button>
          </div>
        </div>
      )}

      {/* Upload Zone - shown when no image */}
      {!value && (
        <div
          {...getRootProps()}
          className={`${getAspectClass()} border-2 border-dashed rounded-sm flex flex-col items-center justify-center cursor-pointer transition-colors ${
            isDragActive 
              ? 'border-primary bg-primary/5' 
              : 'border-muted-foreground/30 hover:border-primary hover:bg-muted/50'
          } ${uploading ? 'pointer-events-none' : ''}`}
        >
          <input {...getInputProps()} id={`upload-input-${label}`} />
          
          {uploading ? (
            <div className="text-center p-4">
              <Loader2 className="mx-auto mb-3 animate-spin text-muted-foreground" size={32} />
              <Progress value={uploadProgress} className="w-32 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">{uploadProgress}%</p>
            </div>
          ) : (
            <div className="text-center p-4">
              <Upload className="mx-auto mb-3 text-muted-foreground" size={32} />
              <p className="text-sm font-medium mb-1">
                {isDragActive ? 'Rilascia qui' : 'Trascina un\'immagine'}
              </p>
              <p className="text-xs text-muted-foreground mb-3">
                oppure clicca per selezionare
              </p>
              <p className="text-xs text-muted-foreground">
                JPEG, PNG, WebP • Max {maxSize}MB
              </p>
            </div>
          )}
        </div>
      )}

      {/* Hidden input for replace button */}
      {value && (
        <input
          {...getInputProps()}
          id={`upload-input-${label}`}
          className="hidden"
        />
      )}

      {/* URL Fallback Option */}
      {showUrlFallback && !value && (
        <div className="mt-3">
          {showUrlInput ? (
            <div className="flex gap-2">
              <Input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://esempio.com/immagine.jpg"
                className="text-sm"
              />
              <Button type="button" size="sm" onClick={handleUrlSubmit}>
                OK
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setShowUrlInput(false)}>
                <X size={14} />
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowUrlInput(true)}
              className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              oppure inserisci URL esterno
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * MultiImageUploader - Per upload multiplo con stato individuale per ogni immagine
 * 
 * Features:
 * - Mostra stato individuale per ogni file (uploading/success/failed)
 * - Pulsante retry per immagini fallite
 * - Impedisce il salvataggio se ci sono upload falliti
 */
export function MultiImageUploader({
  value = [],
  onChange,
  label = "Immagini",
  maxImages = 50,
  maxSize = 20,
  className = "",
  onUploadStateChange  // Callback per comunicare lo stato degli upload al parent
}) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  // Stato individuale per ogni file in upload
  const [fileStates, setFileStates] = useState([]);
  // Files falliti per retry
  const [failedFiles, setFailedFiles] = useState([]);

  // Notifica il parent dello stato
  const notifyParent = useCallback((states) => {
    if (onUploadStateChange) {
      const hasFailures = states.some(s => s.status === 'failed');
      const isUploading = states.some(s => s.status === 'uploading');
      onUploadStateChange({ hasFailures, isUploading, states });
    }
  }, [onUploadStateChange]);

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(r => r.errors[0]?.message).join(', ');
      toast.error(`Alcuni file non sono validi: ${errors}`);
    }

    if (acceptedFiles.length === 0) return;

    // Check max images limit
    if (value.length + acceptedFiles.length > maxImages) {
      toast.error(`Puoi caricare massimo ${maxImages} immagini`);
      return;
    }

    // Initialize file states
    const initialStates = acceptedFiles.map(file => ({
      name: file.name,
      size: file.size,
      status: 'uploading', // uploading | success | failed
      error: null,
      file: file
    }));
    setFileStates(initialStates);
    notifyParent(initialStates);
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    acceptedFiles.forEach(file => {
      formData.append('files', file);
    });

    try {
      const response = await api.post('/admin/upload/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      const { uploaded, errors, summary } = response.data;

      // Update file states based on response
      const updatedStates = initialStates.map(state => {
        const successItem = uploaded.find(u => u.original_filename === state.name);
        const errorItem = errors.find(e => e.filename === state.name);
        
        if (successItem) {
          return { ...state, status: 'success', uploadedData: successItem };
        } else if (errorItem) {
          return { ...state, status: 'failed', error: errorItem.error };
        }
        return state;
      });
      
      setFileStates(updatedStates);
      notifyParent(updatedStates);

      // Save failed files for retry
      const failed = updatedStates.filter(s => s.status === 'failed');
      setFailedFiles(failed);

      if (uploaded.length > 0) {
        const newImages = uploaded.map((img, idx) => ({
          id: img.id,
          url: img.url,
          thumbnail_url: img.thumbnail_url,
          large_url: img.large_url || img.url,
          original_url: img.original_url,
          type: 'image',
          alt_text: img.alt_text || '',
          order: value.length + idx
        }));

        onChange([...value, ...newImages]);
        toast.success(`${uploaded.length}/${summary.total} immagini caricate`);
      }

      if (errors.length > 0) {
        toast.error(`${errors.length} immagini non caricate. Usa il pulsante Riprova.`);
      }
    } catch (error) {
      console.error('Upload error:', error);
      // Mark all as failed
      const failedStates = initialStates.map(s => ({
        ...s, 
        status: 'failed', 
        error: error.response?.data?.detail || 'Errore di rete'
      }));
      setFileStates(failedStates);
      setFailedFiles(failedStates);
      notifyParent(failedStates);
      toast.error('Errore nel caricamento');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  }, [value, onChange, maxImages, notifyParent]);

  // Retry failed uploads
  const retryFailed = useCallback(async () => {
    if (failedFiles.length === 0) return;
    
    const filesToRetry = failedFiles.filter(f => f.file).map(f => f.file);
    if (filesToRetry.length === 0) {
      toast.error('Impossibile riprovare - ricarica i file');
      return;
    }

    // Reset failed files and retry
    setFailedFiles([]);
    await onDrop(filesToRetry, []);
  }, [failedFiles, onDrop]);

  // Clear all states
  const clearStates = () => {
    setFileStates([]);
    setFailedFiles([]);
    notifyParent([]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp']
    },
    maxSize: maxSize * 1024 * 1024,
    multiple: true
  });

  const successCount = fileStates.filter(s => s.status === 'success').length;
  const failedCount = fileStates.filter(s => s.status === 'failed').length;
  const uploadingCount = fileStates.filter(s => s.status === 'uploading').length;

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-medium mb-2">
          {label} ({value.length}/{maxImages})
        </label>
      )}

      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-sm p-8 text-center cursor-pointer transition-colors ${
          isDragActive 
            ? 'border-primary bg-primary/5' 
            : 'border-muted-foreground/30 hover:border-primary hover:bg-muted/50'
        } ${uploading ? 'pointer-events-none' : ''}`}
      >
        <input {...getInputProps()} />
        
        {uploading ? (
          <div>
            <Loader2 className="mx-auto mb-3 animate-spin text-muted-foreground" size={32} />
            <Progress value={uploadProgress} className="w-48 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              Caricamento {fileStates.length} immagini... {uploadProgress}%
            </p>
          </div>
        ) : (
          <>
            <Upload className="mx-auto mb-3 text-muted-foreground" size={32} />
            <p className="text-sm font-medium mb-1">
              {isDragActive ? 'Rilascia le immagini qui' : 'Trascina le immagini qui'}
            </p>
            <p className="text-xs text-muted-foreground mb-2">
              oppure clicca per selezionare (selezione multipla)
            </p>
            <p className="text-xs text-muted-foreground">
              JPEG, PNG, WebP • Max {maxSize}MB per file
            </p>
          </>
        )}
      </div>

      {/* Upload Status Panel - Shows individual file states */}
      {fileStates.length > 0 && (
        <div className="mt-4 border border-border rounded-sm overflow-hidden">
          <div className="bg-muted px-3 py-2 flex items-center justify-between">
            <span className="text-sm font-medium">
              Stato Upload: {successCount} ✓ {failedCount > 0 && <span className="text-red-500">{failedCount} ✗</span>}
            </span>
            <div className="flex gap-2">
              {failedCount > 0 && (
                <Button 
                  type="button" 
                  size="sm" 
                  variant="outline"
                  onClick={retryFailed}
                  className="text-xs"
                >
                  <AlertCircle size={12} className="mr-1" />
                  Riprova falliti ({failedCount})
                </Button>
              )}
              <Button 
                type="button" 
                size="sm" 
                variant="ghost"
                onClick={clearStates}
                className="text-xs"
              >
                <X size={12} className="mr-1" />
                Chiudi
              </Button>
            </div>
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-border">
            {fileStates.map((state, idx) => (
              <div 
                key={`${state.name || 'file'}-${idx}`} 
                className={`px-3 py-2 flex items-center gap-3 text-sm ${
                  state.status === 'failed' ? 'bg-red-50' : 
                  state.status === 'success' ? 'bg-green-50' : ''
                }`}
              >
                {/* Status Icon */}
                <div className="flex-shrink-0">
                  {state.status === 'uploading' && (
                    <Loader2 size={16} className="animate-spin text-blue-500" />
                  )}
                  {state.status === 'success' && (
                    <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                      <span className="text-white text-xs">✓</span>
                    </div>
                  )}
                  {state.status === 'failed' && (
                    <div className="w-4 h-4 rounded-full bg-red-500 flex items-center justify-center">
                      <span className="text-white text-xs">✗</span>
                    </div>
                  )}
                </div>
                
                {/* File Info */}
                <div className="flex-1 min-w-0">
                  <p className="truncate font-medium">{state.name}</p>
                  {state.error && (
                    <p className="text-xs text-red-600 truncate">{state.error}</p>
                  )}
                </div>
                
                {/* File Size */}
                <span className="text-xs text-muted-foreground flex-shrink-0">
                  {(state.size / 1024 / 1024).toFixed(1)}MB
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Warning if there are failed uploads */}
      {failedCount > 0 && !uploading && (
        <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-sm">
          <p className="text-sm text-yellow-800 flex items-center gap-2">
            <AlertCircle size={16} />
            <span>
              <strong>{failedCount} immagini non caricate.</strong> Usa "Riprova falliti" prima di salvare.
            </span>
          </p>
        </div>
      )}

      {/* Info */}
      {value.length > 0 && (
        <p className="text-xs text-muted-foreground mt-2">
          {value.length} immagini caricate. Trascina per riordinare nel pannello sopra.
        </p>
      )}
    </div>
  );
}
